import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { commonApi, holdingsApi, companiesApi } from '@/services/api';
import { Holdings } from '@/types/client';
import { useClientApi } from '@/hooks/useClientApi';
import shared from '@/components/utils/shared.module.css';
import DataTable, { ColumnDef } from '@/components/utils/DataTable';
import { FilterAsyncSelect, Button } from '@/components/ui';
import type { FilterOption } from '@/components/ui';

type SelectOption = FilterOption;

const PAGE_SIZE = 20;

type SortField = 'client_master__client_code' | 'company__symbol' | 'adj_quantity' | 'adj_unit_cost' | 'adj_unit_price';

const HoldingsList: React.FC = () => {
  const { getClients, getClientFilter } = useClientApi();
  const [holdings, setHoldings] = useState<Holdings[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [selectedClient, setSelectedClient] = useState<SelectOption | null>(null);
  const [selectedCompany, setSelectedCompany] = useState<SelectOption | null>(null);
  const [downloadingCSV, setDownloadingCSV] = useState(false);
  const [sortField, setSortField] = useState<SortField>('client_master__client_code');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const navigate = useNavigate();

  const getClientFullName = (holding: Holdings): string => {
    const nameParts = [];
    if (holding.client_first_name) nameParts.push(holding.client_first_name);
    if (holding.client_middle_name) nameParts.push(holding.client_middle_name);
    if (holding.client_last_name) nameParts.push(holding.client_last_name);
    return nameParts.length > 0 ? nameParts.join(' ') : holding.client_code;
  };

  useEffect(() => {
    const fetchLatestHoldingDate = async () => {
      try {
        const data = await commonApi.getLastWorkingDay();
        if (data.last_working_day) {
          setSelectedDate(data.last_working_day);
        } else {
          setLoading(false);
        }
      } catch (error) {
        console.error('Error fetching latest holding date:', error);
        setLoading(false);
      }
    };
    fetchLatestHoldingDate();
  }, []);

  const loadClientOptions = useCallback(async (inputValue: string): Promise<SelectOption[]> => {
    try {
      const response = await getClients({ search: inputValue, page_size: 50 });
      return response.results.map(client => ({
        value: client.id.toString(),
        label: `${client.client_code} - ${client.full_name || `${client.first_name} ${client.last_name}`}`,
      }));
    } catch {
      return [];
    }
  }, []);

  const loadCompanyOptions = useCallback(async (inputValue: string): Promise<SelectOption[]> => {
    try {
      const response = await companiesApi.getCompanies({ search: inputValue, page_size: 50 });
      return response.results.map(company => ({
        value: company.id.toString(),
        label: `${company.symbol} - ${company.name}`,
      }));
    } catch {
      return [];
    }
  }, []);

  const loadHoldings = useCallback(async () => {
    if (!selectedDate) return;
    try {
      setLoading(true);
      setError(null);
      const clientVal = selectedClient ? parseInt(selectedClient.value) : undefined;
      const response = await holdingsApi.getHoldings({
        date: selectedDate,
        page: currentPage,
        page_size: PAGE_SIZE,
        ...getClientFilter(clientVal),
        company: selectedCompany ? parseInt(selectedCompany.value) : undefined,
        search: searchQuery.trim() || undefined,
        ordering: `${sortDir === 'desc' ? '-' : ''}${sortField}`
      });
      setHoldings(response.results);
      setTotalCount(response.count);
      setTotalPages(Math.ceil(response.count / 20));
    } catch (err: any) {
      console.error('Error loading holdings:', err);
      setError(err.response?.data?.message || 'Failed to load holdings');
    } finally {
      setLoading(false);
    }
  }, [selectedDate, currentPage, selectedClient, selectedCompany, searchQuery, sortField, sortDir, getClientFilter]);

  useEffect(() => {
    if (selectedDate) loadHoldings();
  }, [loadHoldings, selectedDate]);

  const formatNumber = (value: number | undefined, decimals = 2): string => {
    if (value === undefined || value === null) return '-';
    return new Intl.NumberFormat('en-IN', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    }).format(value);
  };

  const formatDate = (dateString: string): string =>
    new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric'
    });

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field as SortField);
      setSortDir('asc');
    }
    setCurrentPage(1);
  };

  const hasActiveFilters = !!(selectedClient || selectedCompany || searchQuery.trim());

  const clearFilters = () => {
    setSelectedClient(null);
    setSelectedCompany(null);
    setSearchQuery('');
    setCurrentPage(1);
  };

  const downloadCSV = async () => {
    setDownloadingCSV(true);
    try {
      const clientVal = selectedClient ? parseInt(selectedClient.value) : undefined;
      const response = await holdingsApi.exportAllHoldings({
        date: selectedDate,
        ...getClientFilter(clientVal),
        company: selectedCompany ? parseInt(selectedCompany.value) : undefined,
        search: searchQuery.trim() || undefined,
        ordering: `${sortDir === 'desc' ? '-' : ''}${sortField}`
      });

      const allHoldings = response.results;
      const headers = [
        'Client Code', 'Client Name', 'Symbol', 'Company',
        'Quantity', 'Unit Cost', 'Unit Price', 'Exchange Rate',
        'Base Currency', 'Currency', 'Accrued Income', '% of Portfolio'
      ];
      const rows = allHoldings.map(holding => {
        const clientName = [holding.client_first_name, holding.client_middle_name, holding.client_last_name]
          .filter(Boolean).join(' ') || holding.client_code;
        return [
          holding.client_code, clientName, holding.company_symbol, holding.company_name,
          holding.adj_quantity, holding.adj_unit_cost, holding.adj_unit_price,
          holding.exchange_rate, holding.base_currency_code, holding.currency_code,
          holding.accrued_income || 0, holding.percentage_of_portfolio || 0
        ].map(val => `"${val}"`).join(',');
      });

      const csvContent = [headers.join(','), ...rows].join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `holdings_${selectedDate}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err: any) {
      console.error('Error downloading CSV:', err);
      setError(err.response?.data?.message || err.response?.data?.detail || err.message || 'Failed to download CSV');
    } finally {
      setDownloadingCSV(false);
    }
  };

  const activeFilterCount = [selectedClient, selectedCompany].filter(Boolean).length;
  const columns: ColumnDef<Holdings>[] = [
    { key: 'client_master__client_code', header: 'Client Code', sortable: true,
      cellClassName: () => shared['cell-strong'], render: r => r.client_code },
    { key: 'client_name', header: 'Client Name', width: '160px',
      cellClassName: () => shared['col-mid'], render: r => getClientFullName(r) },
    { key: 'company__symbol', header: 'Symbol', sortable: true,
      render: r => (
        <Link to={`/companies/${r.company}`} className={shared.link}>
          {r.company_symbol}
        </Link>
      ) },
    { key: 'company_name', header: 'Company', width: '160px',
      cellClassName: () => shared['col-mid'], render: r => r.company_name },
    { key: 'adj_quantity', header: 'Quantity', sortable: true, align: 'right',
      render: r => formatNumber(r.adj_quantity, 3) },
    { key: 'adj_unit_cost', header: 'Unit Cost', sortable: true, align: 'right',
      render: r => formatNumber(r.adj_unit_cost, 4) },
    { key: 'adj_unit_price', header: 'Unit Price', sortable: true, align: 'right',
      render: r => formatNumber(r.adj_unit_price, 4) },
    { key: 'exchange_rate', header: 'Exchange Rate', align: 'right',
      render: r => formatNumber(r.exchange_rate, 4) },
    { key: 'base_currency_code', header: 'BC', tooltip: 'Base Currency',
      render: r => r.base_currency_code },
    { key: 'currency_code', header: 'Currency', render: r => r.currency_code },
    { key: 'accrued_income', header: 'AI', tooltip: 'Accrued Income', align: 'right',
      render: r => formatNumber(r.accrued_income || 0) },
    { key: 'percentage_of_portfolio', header: '% of Portfolio', align: 'right',
      render: r => `${formatNumber(r.percentage_of_portfolio)}%` },
    { key: 'actions', header: 'Actions', width: '70px',
      render: r => (
        <Button size="sm" onClick={() => navigate(`/holdings/client/${r.client_master}?date=${selectedDate}`)}>
          View
        </Button>
      ) },
  ];

  return (
    <DataTable<Holdings>
      title="Holdings"
      subtitle={`Client positions as on ${selectedDate ? formatDate(selectedDate) : 'the selected date'}`}
      data={holdings}
      columns={columns}
      rowKey={row => row.id}
      totalCount={totalCount}
      loading={loading}
      loadingMessage="Loading holdings…"
      error={error}
      currentPage={currentPage}
      totalPages={totalPages}
      onPageChange={setCurrentPage}
      pageSize={PAGE_SIZE}
      sortField={sortField}
      sortDir={sortDir}
      onSort={handleSort}
      searchQuery={searchQuery}
      onSearchChange={value => { setSearchQuery(value); setCurrentPage(1); }}
      searchPlaceholder="Search by client code, name, symbol, company…"
      hasActiveFilters={hasActiveFilters}
      activeFilterCount={activeFilterCount}
      onClearFilters={clearFilters}
      onDownloadCSV={downloadCSV}
      isDownloading={downloadingCSV}
      emptyMessage={`No holdings found for ${selectedDate ? formatDate(selectedDate) : 'the selected date'}.`}
      filterSlot={
        <>
          <div className={shared['filter-group']}>
            <label htmlFor="holding-date">Holdings Date</label>
            <input
              id="holding-date"
              type="date"
              value={selectedDate}
              onChange={(e) => { setSelectedDate(e.target.value); setCurrentPage(1); setSelectedCompany(null); }}
              className={shared.input}
            />
          </div>

          <FilterAsyncSelect
            label="Client"
            value={selectedClient}
            onChange={opt => { setSelectedClient(opt); setCurrentPage(1); setSelectedCompany(null); }}
            loadOptions={loadClientOptions}
            placeholder="All Clients"
          />

          <FilterAsyncSelect
            label="Company"
            value={selectedCompany}
            onChange={opt => { setSelectedCompany(opt); setCurrentPage(1); }}
            loadOptions={loadCompanyOptions}
            placeholder="All Companies"
          />
        </>
      }
    />
  );
};

export default HoldingsList;
